<script lang="ts" setup>
export interface UIStep {
    label: string;
    title: string;
    description?: string;
    key?: string | number;
    onNext?: () => boolean;
    onPrevious?: () => boolean;
}

export interface UIStepsProps {
    disableCompleted?: boolean;
    steps: UIStep[];
}

const active = defineModel<string | number>("active", { required: true });
const props = withDefaults(defineProps<UIStepsProps>(), {
    disableCompleted: false,
});

function next(step: UIStep) {
    if (step.onNext ? step.onNext() : true) {
        active.value = step.key ?? step.label;
    }
}

function previous(step: UIStep) {
    if (step.onPrevious ? step.onPrevious() : true) {
        active.value = step.key ?? step.label;
    }
}

const mappedSteps = computed(() => {
    let foundCurrent = false;

    return props.steps.map((step) => {
        if ((step.key ?? step.label) === active.value) foundCurrent = true;

        return {
            uuid: step.key ?? createUuid(),
            done: !foundCurrent,
            ...step,
        };
    });
});

const activeStep = computed(() =>
    mappedSteps.value.find((step) => step.key === active.value),
);

const activeStepIndex = computed(() =>
    mappedSteps.value.findIndex((step) => step.key === active.value),
);
</script>

<template>
    <ol
        role="list"
        class="@container flex gap-x-4 @3xl:gap-y-4 relative flex-wrap"
    >
        <li v-for="step in mappedSteps" :key="step.uuid" class="flex-1">
            <button
                v-if="step.done"
                :disabled="disableCompleted"
                class="group w-full flex flex-col border-primary-500 hover:border-primary-600 border-l-0 border-t-4 pb-0 pl-0 pt-4 text-left"
                @click="previous(step)"
            >
                <div
                    class="flex-col"
                    :class="active === step.key ? 'flex' : 'hidden @3xl:flex'"
                >
                    <slot
                        :step="step"
                        :next="() => next(step)"
                        :previous="() => previous(step)"
                    >
                        <span
                            v-if="step.label"
                            class="text-sm font-medium text-primary-600"
                            >{{ step.label }}</span
                        >
                        <span v-if="step.title" class="text-sm font-medium">{{
                            step.title
                        }}</span>
                        <span
                            v-if="step.description"
                            class="text-sm opacity-50"
                            >{{ step.description }}</span
                        >
                    </slot>
                </div>
            </button>
            <div
                v-else-if="active === step.key"
                class="border-primary-500 border-l-0 border-t-4 pb-0 pl-0 pt-4"
                aria-current="step"
            >
                <div class="flex-col hidden @3xl:flex">
                    <slot
                        :step="step"
                        :next="() => next(step)"
                        :previous="() => previous(step)"
                    >
                        <span
                            v-if="step.label"
                            class="text-sm font-medium text-primary-600"
                            >{{ step.label }}</span
                        >
                        <span v-if="step.title" class="text-sm font-medium">{{
                            step.title
                        }}</span>
                        <span
                            v-if="step.description"
                            class="text-sm opacity-50"
                            >{{ step.description }}</span
                        >
                    </slot>
                </div>
            </div>
            <button
                v-else
                class="group flex w-full flex-col border-gray-200 hover:border-gray-300 border-l-0 border-t-4 pb-0 pl-0 pt-4 text-left"
                @click="next(step)"
            >
                <div class="flex-col hidden @3xl:flex">
                    <slot
                        :step="step"
                        :next="() => next(step)"
                        :previous="() => previous(step)"
                    >
                        <span
                            class="text-sm font-medium text-gray-500 group-hover:text-gray-700"
                            >{{ step.label }}</span
                        >
                        <span class="text-sm font-medium">{{
                            step.title
                        }}</span>
                        <span
                            v-if="step.description"
                            class="text-sm opacity-50"
                            >{{ step.description }}</span
                        >
                    </slot>
                </div>
            </button>
        </li>
        <li v-if="activeStep" class="w-full @3xl:hidden">
            <div>
                <slot
                    :step="activeStep"
                    :next="
                        () =>
                            mappedSteps[activeStepIndex + 1]
                                ? next(mappedSteps[activeStepIndex + 1]!)
                                : undefined
                    "
                    :previous="
                        () =>
                            mappedSteps[activeStepIndex - 1]
                                ? previous(mappedSteps[activeStepIndex - 1]!)
                                : undefined
                    "
                >
                    <div class="flex flex-col">
                        <span
                            class="text-sm font-medium text-gray-500 group-hover:text-gray-700"
                            >{{ activeStep.label }}</span
                        >
                        <span class="text-sm font-medium">{{
                            activeStep.title
                        }}</span>
                        <span
                            v-if="activeStep.description"
                            class="text-sm opacity-50"
                            >{{ activeStep.description }}</span
                        >
                    </div>
                </slot>
            </div>
        </li>
    </ol>
</template>
